import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  Receipt as ReceiptIcon,
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  Cancel as OverdueIcon,
  PauseCircle as PartialPaidIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  Block as VoidIcon,
  LocalOffer as WaiverIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import invoiceService from '../services/invoiceService';
import tenantService from '../services/tenantService';
import paymentService from '../services/paymentService';
import penaltyService from '../services/penaltyService';
import { PageHeader, Loading, EmptyState } from '../components/Layout';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [penalties, setPenalties] = useState({
    totalAmount: 0,
    totalCount: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [waiverDialog, setWaiverDialog] = useState(false);
  const [waiverAmount, setWaiverAmount] = useState('');
  const [waiverReason, setWaiverReason] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    room_id: '',
    amount: '',
    due_date: '',
    description: '',
  });
  const [paymentFormData, setPaymentFormData] = useState({
    invoice_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    transaction_ref: '',
    notes: '',
  });

  useEffect(() => {
    fetchInvoices();
    fetchTenants();
    fetchPenalties();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const [invoicesRes, penaltiesRes] = await Promise.all([
        invoiceService.getAllInvoices(),
        penaltyService.getAllPenalties().catch(() => ({ data: [] }))
      ]);
      
      const invoicesData = invoicesRes.data || [];
      const penaltiesData = penaltiesRes.data || [];
      
      // Create a map of invoice_id to total penalty amount
      const penaltyMap = {};
      penaltiesData.forEach(penalty => {
        const invoiceId = penalty.invoice_id;
        if (!penaltyMap[invoiceId]) {
          penaltyMap[invoiceId] = 0;
        }
        penaltyMap[invoiceId] += parseFloat(penalty.penalty_amount || 0);
      });
      
      // Merge penalty data into invoices
      const invoicesWithPenalties = invoicesData.map(invoice => ({
        ...invoice,
        penalty_amount: penaltyMap[invoice.id] || 0,
        total_with_penalty: (parseFloat(invoice.amount) || 0) + (penaltyMap[invoice.id] || 0)
      }));
      
      setInvoices(invoicesWithPenalties);
    } catch (error) {
      toast.error('Failed to load invoices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchPenalties = async () => {
    try {
      const response = await penaltyService.getPenaltiesSummary();
      setPenalties({
        totalAmount: response.data.total_amount || 0,
        totalCount: response.data.total_penalties || 0,
      });
    } catch (error) {
      console.error('Failed to load penalties:', error);
      setPenalties({
        totalAmount: 0,
        totalCount: 0,
      });
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      tenant_id: '',
      room_id: '',
      amount: '',
      due_date: '',
      description: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    const tenant = tenants.find(t => t.id === parseInt(tenantId));
    setFormData({
      ...formData,
      tenant_id: tenantId,
      room_id: tenant?.current_room_id || '',
    });
  };

  const handleGenerateInvoice = async () => {
    // Validate required fields
    if (!formData.tenant_id || !formData.amount || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.room_id) {
      toast.error('Selected tenant does not have a room assigned');
      return;
    }

    try {
      await invoiceService.generateInvoice({
        tenant_id: parseInt(formData.tenant_id),
        room_id: parseInt(formData.room_id),
        amount: parseFloat(formData.amount),
        due_date: formData.due_date,
        description: formData.description || null,
      });
      toast.success('Invoice generated successfully');
      handleCloseDialog();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
      console.error(error);
    }
  };

  const handleGenerateMonthly = async () => {
    if (window.confirm('Generate monthly rent invoices for all occupied rooms?')) {
      try {
        const response = await invoiceService.generateMonthlyInvoices();
        const data = response.data;
        
        // Handle new response format
        if (data.summary) {
          const { invoices_created, skipped_count, total_rooms } = data.summary;
          
          if (invoices_created > 0) {
            // Build message with details about skipped tenants
            let message = `✅ Generated ${invoices_created} invoice(s) successfully!`;
            
            if (skipped_count > 0) {
              message += `\n\n⚠️ Skipped ${skipped_count} tenant(s):`;
              
              // Show reasons for first few skipped tenants
              const skippedToShow = data.skipped?.slice(0, 3) || [];
              skippedToShow.forEach(skip => {
                message += `\n• Room ${skip.room_number}: ${skip.reason}`;
              });
              
              if (data.skipped?.length > 3) {
                message += `\n...and ${data.skipped.length - 3} more`;
              }
            }
            
            toast.success(message, { autoClose: 8000 });
          } else if (skipped_count > 0) {
            // All tenants were skipped - show why
            let message = `ℹ️ No new invoices created. All ${skipped_count} tenant(s) already have invoices for the current month.\n\n`;
            
            // Show reasons
            const skippedToShow = data.skipped?.slice(0, 3) || [];
            skippedToShow.forEach(skip => {
              message += `\n• Room ${skip.room_number}${skip.tenant_name ? ` (${skip.tenant_name})` : ''}: ${skip.reason}`;
            });
            
            if (data.skipped?.length > 3) {
              message += `\n...and ${data.skipped.length - 3} more`;
            }
            
            toast.info(message, { autoClose: 8000 });
          } else {
            toast.info('ℹ️ No occupied rooms found to generate invoices.');
          }
          
          // Log full details for debugging
          console.log('Invoice Generation Results:', data);
        } else {
          // Fallback for old response format
          toast.success(`Generated ${response.data.count || 0} invoices successfully`);
        }
        
        fetchInvoices();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to generate monthly invoices');
        console.error(error);
      }
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await invoiceService.sendReminders();
      toast.success(`Sent ${response.data.sent || 0} reminders successfully`);
    } catch (error) {
      toast.error('Failed to send reminders');
      console.error(error);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setSelectedInvoice(null);
  };

  const handleOpenPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    // Calculate correct remaining balance including penalty
    const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
    const remainingBalance = totalAmount - (invoice.amount_paid || 0);
    setPaymentFormData({
      invoice_id: invoice.id,
      amount: remainingBalance || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      transaction_ref: '',
      notes: '',
    });
    setPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog(false);
    setSelectedInvoice(null);
    setPaymentFormData({
      invoice_id: '',
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      transaction_ref: '',
      notes: '',
    });
  };

  const handleApplyCredits = async (invoice) => {
    try {
      const response = await paymentService.applyCreditsToInvoice(invoice.id);
      const result = response.data;
      
      if (result.success) {
        toast.success(
          `✅ Applied KES ${result.credit_applied.toLocaleString()} credit to invoice ${invoice.invoice_number}! Remaining credit balance: KES ${result.remaining_credit_balance.toLocaleString()}`,
          { autoClose: 8000 }
        );
        handleCloseViewDialog();
        fetchInvoices();
      } else {
        toast.info(result.message || 'No credits applied');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to apply credits');
      console.error(error);
    }
  };

  const handlePaymentInputChange = (e) => {
    setPaymentFormData({
      ...paymentFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenWaiverDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setWaiverAmount('');
    setWaiverReason('');
    setWaiverDialog(true);
  };

  const handleApplyWaiver = async () => {
    const amount = parseFloat(waiverAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid waiver amount');
      return;
    }
    if (!waiverReason.trim()) {
      toast.error('Please provide a reason for the waiver');
      return;
    }
    try {
      const response = await invoiceService.applyWaiver(selectedInvoice.id, amount, waiverReason);
      toast.success(response.data.message, { autoClose: 8000 });
      setWaiverDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply waiver');
    }
  };

  const handleOpenVoidDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setVoidReason('');
    setVoidDialog(true);
  };

  const handleVoidInvoice = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason for voiding');
      return;
    }
    try {
      const response = await invoiceService.voidInvoice(selectedInvoice.id, voidReason);
      const data = response.data;
      let msg = `Invoice ${selectedInvoice.invoice_number} voided.`;
      if (data.payments_voided > 0) {
        msg += ` ${data.payments_voided} payment(s) reversed (KES ${data.total_amount_reversed?.toLocaleString()}).`;
      }
      toast.success(msg, { autoClose: 8000 });
      setVoidDialog(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void invoice');
    }
  };

  const handleSubmitPayment = async () => {
    if (paymentSubmitting) return; // Prevent double-click
    try {
      // Validate amount
      const paymentAmount = parseFloat(paymentFormData.amount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        toast.error('Please enter a valid payment amount');
        return;
      }

      // M-Pesa transaction code is mandatory
      if (paymentFormData.payment_method === 'mpesa' && !paymentFormData.transaction_ref.trim()) {
        toast.error('M-Pesa transaction code is required');
        return;
      }

      // Calculate correct remaining balance including penalty
      const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
      const remainingBalance = totalAmount - (selectedInvoice.amount_paid || 0);
      
      // Allow overpayment (advance/upfront payment) with confirmation
      if (paymentAmount > remainingBalance) {
        const overpaymentAmount = paymentAmount - remainingBalance;
        const confirmed = window.confirm(
          `This payment (KES ${paymentAmount.toLocaleString()}) exceeds the invoice balance (KES ${remainingBalance.toLocaleString()}) by KES ${overpaymentAmount.toLocaleString()}.\n\n` +
          `The excess amount will be applied as a credit to the tenant's account for future invoices.\n\n` +
          `Do you want to continue?`
        );
        if (!confirmed) return;
      }

      setPaymentSubmitting(true);

      // Prepare payload matching backend schema
      const payload = {
        invoice_id: paymentFormData.invoice_id,
        amount: paymentAmount,
        payment_method: paymentFormData.payment_method,
        payment_date: paymentFormData.payment_date,
        transaction_reference: paymentFormData.transaction_ref.trim() || `MANUAL-${Date.now()}`,
      };
      
      console.log('Submitting payment:', payload);
      
      // Submit payment
      const response = await paymentService.submitManualPayment(payload);
      
      console.log('Payment response:', response.data);
      
      // Show success message with credit information if applicable
      const data = response.data;
      if (data.credit_added > 0) {
        toast.success(
          `✅ Payment recorded! KES ${data.credit_added.toLocaleString()} added to tenant's credit balance. New credit balance: KES ${data.current_credit_balance.toLocaleString()}`,
          { autoClose: 8000 }
        );
      } else {
        toast.success('Payment recorded successfully');
      }
      
      handleClosePaymentDialog();
      fetchInvoices();
    } catch (error) {
      console.error('Payment submission error:', error);
      
      let errorMessage = 'Failed to record payment';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const responseData = error.response.data;
        
        // Check for validation errors or detail messages
        if (typeof responseData === 'object') {
          if (responseData.detail) {
            const detail = Array.isArray(responseData.detail) 
              ? responseData.detail.map(e => e.msg || e).join(', ')
              : responseData.detail;
            
            // Special handling for "already fully paid" error when there are penalties
            if (detail.toLowerCase().includes('already fully paid') || detail.toLowerCase().includes('already paid')) {
              const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
              const remainingBalance = totalAmount - (selectedInvoice.amount_paid || 0);
              
              if (remainingBalance > 0 && selectedInvoice.penalty_amount > 0) {
                errorMessage = `Backend Error: The system says this invoice is paid, but there's still a penalty balance of KES ${selectedInvoice.penalty_amount.toLocaleString()}. This is a backend bug - the system should allow payment of penalties. Please contact the system administrator.`;
              } else {
                errorMessage = detail;
              }
            } else {
              errorMessage = detail;
            }
          } else if (responseData.message) {
            errorMessage = responseData.message;
          } else {
            // Try to extract validation errors
            const validationErrors = Object.entries(responseData)
              .filter(([key]) => key !== 'status')
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('; ');
            errorMessage = validationErrors || `Server error: ${error.response.status}`;
          }
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response from server. Please check if the backend is running.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message || 'An error occurred while recording payment';
      }
      
      toast.error(errorMessage);
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial_paid':
        return 'info';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'voided':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <PaidIcon fontSize="small" />;
      case 'partial_paid':
        return <PartialPaidIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'overdue':
        return <OverdueIcon fontSize="small" />;
      case 'voided':
        return <VoidIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Calculate statistics based on actual balances including penalties
  const totalInvoices = invoices.length;
  
  // Fully paid: no remaining balance (including penalties)
  const paidInvoices = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return balance <= 0;
  }).length;
  
  // Partial paid: has payment and remaining balance
  const partialPaidInvoices = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return i.amount_paid > 0 && balance > 0;
  }).length;
  
  // Not paid: no payment made and has balance
  const pendingInvoices = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return (i.amount_paid || 0) === 0 && balance > 0 && i.status !== 'overdue';
  }).length;
  
  // Overdue: based on status
  const overdueInvoices = invoices.filter(i => i.status === 'overdue').length;
  
  // Total amount including penalties
  const totalAmount = invoices.reduce((sum, i) => {
    const total = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    return sum + (total || 0);
  }, 0);
  
  // Amount paid for fully paid invoices
  const paidAmount = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return balance <= 0;
  }).reduce((sum, i) => sum + (i.amount_paid || 0), 0);
  
  // Amount paid for partial paid invoices
  const partialPaidAmount = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return i.amount_paid > 0 && balance > 0;
  }).reduce((sum, i) => sum + (i.amount_paid || 0), 0);
  
  // Remaining balance for not paid invoices
  const pendingAmount = invoices.filter(i => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return (i.amount_paid || 0) === 0 && balance > 0 && i.status !== 'overdue';
  }).reduce((sum, i) => {
    const total = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    return sum + (total || 0);
  }, 0);
  
  // Remaining balance for overdue invoices
  const overdueAmount = invoices.filter(i => i.status === 'overdue').reduce((sum, i) => {
    const totalAmount = i.penalty_amount > 0 ? i.total_with_penalty : i.amount;
    const balance = totalAmount - (i.amount_paid || 0);
    return sum + (balance > 0 ? balance : 0);
  }, 0);

  return (
    <Box>
      <PageHeader
        title="Invoices"
        subtitle="Manage and track property invoices"
        breadcrumbs={[{ label: 'Invoices' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleSendReminders}
            >
              Send Reminders
            </Button>
            <Button
              variant="outlined"
              onClick={handleGenerateMonthly}
            >
              Generate Monthly
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Create Invoice
            </Button>
          </Box>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total
                  </Typography>
                  <Typography variant="h4">{totalInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {totalAmount.toLocaleString()}
                  </Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Paid
                  </Typography>
                  <Typography variant="h4">{paidInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {paidAmount.toLocaleString()}
                  </Typography>
                </Box>
                <PaidIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Partial Paid
                  </Typography>
                  <Typography variant="h4">{partialPaidInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {partialPaidAmount.toLocaleString()}
                  </Typography>
                </Box>
                <PartialPaidIcon sx={{ fontSize: 48, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Not Paid
                  </Typography>
                  <Typography variant="h4">{pendingInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {pendingAmount.toLocaleString()}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Overdue
                  </Typography>
                  <Typography variant="h4">{overdueInvoices}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {overdueAmount.toLocaleString()}
                  </Typography>
                </Box>
                <OverdueIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: '#fff3e0', border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Late Penalties
                  </Typography>
                  <Typography variant="h4">{penalties.totalCount}</Typography>
                  <Typography variant="caption" color="error.main" fontWeight="bold">
                    KES {penalties.totalAmount.toLocaleString()}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Loading message="Loading invoices..." />
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={ReceiptIcon}
          title="No invoices yet"
          description="Generate invoices for your tenants to start tracking payments."
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleGenerateMonthly}
              >
                Generate Monthly Invoices
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                Create Single Invoice
              </Button>
            </Box>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Invoice #</strong></TableCell>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Room No</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Paid (KES)</strong></TableCell>
                <TableCell><strong>Balance (KES)</strong></TableCell>
                <TableCell><strong>Waiver (KES)</strong></TableCell>
                <TableCell><strong>Penalty (KES)</strong></TableCell>
                <TableCell><strong>Issue Date</strong></TableCell>
                <TableCell><strong>Due Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>#{invoice.invoice_number || invoice.id}</TableCell>
                  <TableCell>{invoice.tenant_name || 'N/A'}</TableCell>
                  <TableCell>{invoice.room_number || 'N/A'}</TableCell>
                  <TableCell>
                    {invoice.penalty_amount > 0 ? (
                      <Box>
                        <Typography variant="body2">
                          {invoice.total_with_penalty?.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({invoice.amount?.toLocaleString()} + {invoice.penalty_amount?.toLocaleString()})
                        </Typography>
                      </Box>
                    ) : (
                      invoice.amount?.toLocaleString()
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography color={invoice.amount_paid > 0 ? 'success.main' : 'text.secondary'}>
                        {(invoice.amount_paid || 0).toLocaleString()}
                      </Typography>
                      {invoice.has_credit_payment && invoice.credit_applied > 0 && (
                        <Typography variant="caption" color="info.main" fontWeight="bold">
                          (Credit: {invoice.credit_applied?.toLocaleString()})
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                      const balance = totalAmount - (invoice.amount_paid || 0);
                      const isOverpaid = balance < 0;
                      const credit = Math.abs(balance);
                      
                      if (isOverpaid) {
                        // Show overpayment as credit
                        return (
                          <Box>
                            <Typography variant="body2" fontWeight="medium" color="success.main">
                              0
                            </Typography>
                            <Typography variant="caption" color="info.main" fontWeight="bold">
                              Credit: {credit.toLocaleString()}
                            </Typography>
                          </Box>
                        );
                      } else {
                        // Show remaining balance
                        return (
                          <Typography 
                            fontWeight="medium"
                            color={balance > 0 ? 'warning.main' : 'success.main'}
                          >
                            {balance.toLocaleString()}
                          </Typography>
                        );
                      }
                    })()}
                  </TableCell>
                  <TableCell>
                    {(invoice.total_waivers || 0) > 0 ? (
                      <Typography fontWeight="bold" color="info.main">
                        -{(invoice.total_waivers || 0).toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice.penalty_amount > 0 ? (
                      <Typography fontWeight="bold" color="error.main">
                        {invoice.penalty_amount?.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(() => {
                        const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                        const balance = totalAmount - (invoice.amount_paid || 0);
                        const isOverpaid = balance < 0;
                        let actualStatus = invoice.status;
                        
                        // Recalculate status based on actual balance including penalties
                        if (balance <= 0) {
                          actualStatus = 'paid';
                        } else if (invoice.amount_paid > 0 && balance > 0) {
                          actualStatus = 'partial_paid';
                        }
                        
                        return (
                          <>
                            <Chip
                              icon={getStatusIcon(actualStatus)}
                              label={actualStatus?.toUpperCase().replace('_', ' ')}
                              color={getStatusColor(actualStatus)}
                              size="small"
                            />
                            {/* Credit Applied Badge */}
                            {invoice.has_credit_payment && (
                              <Chip
                                label="Credit Applied"
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                            {/* Overpayment Badge */}
                            {isOverpaid && (
                              <Chip
                                label="Overpaid"
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      const totalAmount = invoice.penalty_amount > 0 ? invoice.total_with_penalty : invoice.amount;
                      const balance = totalAmount - (invoice.amount_paid || 0);
                      return balance > 0;
                    })() && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenPaymentDialog(invoice)}
                        title="Record Payment"
                      >
                        <PaymentIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewInvoice(invoice)}
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                    {invoice.status !== 'voided' && invoice.status !== 'paid' && (
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleOpenWaiverDialog(invoice)}
                        title="Apply Waiver"
                      >
                        <WaiverIcon />
                      </IconButton>
                    )}
                    {invoice.status !== 'voided' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenVoidDialog(invoice)}
                        title="Void Invoice"
                      >
                        <VoidIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Select Tenant"
            name="tenant_id"
            value={formData.tenant_id}
            onChange={handleTenantChange}
            margin="normal"
            required
          >
            {tenants.length === 0 ? (
              <MenuItem value="">No available tenants</MenuItem>
            ) : (
              tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.full_name} - Room {tenant.room_number || 'No Room'}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            fullWidth
            label="Amount (KES)"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Due Date"
            name="due_date"
            type="date"
            value={formData.due_date}
            onChange={handleInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={3}
            placeholder="Monthly rent, utilities, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleGenerateInvoice} variant="contained" disabled={tenants.length === 0}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialog} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1">
                    #{selectedInvoice.invoice_number || selectedInvoice.id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  {(() => {
                    const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                    const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                    let actualStatus = selectedInvoice.status;
                    
                    // Recalculate status if there's a penalty balance
                    if (balance <= 0) {
                      actualStatus = 'paid';
                    } else if (selectedInvoice.amount_paid > 0 && balance > 0) {
                      actualStatus = 'partial_paid';
                    }
                    
                    return (
                      <Chip
                        icon={getStatusIcon(actualStatus)}
                        label={actualStatus?.toUpperCase().replace('_', ' ')}
                        color={getStatusColor(actualStatus)}
                        size="small"
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Tenant
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.tenant_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Invoice Amount
                  </Typography>
                  <Typography variant="body1">
                    KES {selectedInvoice.amount?.toLocaleString()}
                  </Typography>
                </Grid>
                {selectedInvoice.penalty_amount > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Penalty Charge
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      KES {selectedInvoice.penalty_amount?.toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                {selectedInvoice.penalty_amount > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Total Amount (with penalty)
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      KES {selectedInvoice.total_with_penalty?.toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Amount Paid
                  </Typography>
                  <Box>
                    <Typography variant="body1" color={selectedInvoice.amount_paid > 0 ? 'success.main' : 'text.primary'}>
                      KES {(selectedInvoice.amount_paid || 0).toLocaleString()}
                    </Typography>
                    {selectedInvoice.has_credit_payment && selectedInvoice.credit_applied > 0 && (
                      <Typography variant="caption" color="info.main" fontWeight="bold">
                        (includes KES {selectedInvoice.credit_applied?.toLocaleString()} from credit)
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Remaining Balance
                  </Typography>
                  {(() => {
                    const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                    const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                    const isOverpaid = balance < 0;
                    const credit = Math.abs(balance);
                    
                    if (isOverpaid) {
                      return (
                        <Box>
                          <Typography variant="body1" fontWeight="bold" color="success.main">
                            KES 0
                          </Typography>
                          <Typography variant="caption" color="info.main" fontWeight="bold">
                            Credit Applied: KES {credit.toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Typography 
                          variant="body1" 
                          fontWeight="bold"
                          color={balance > 0 ? 'warning.main' : 'success.main'}
                        >
                          KES {balance.toLocaleString()}
                        </Typography>
                      );
                    }
                  })()}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Issue Date
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.issue_date ? new Date(selectedInvoice.issue_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                {selectedInvoice.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedInvoice.description}
                    </Typography>
                  </Grid>
                )}
                
                {/* Credit & Payment Details Section */}
                {(selectedInvoice.amount_paid > 0) && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, mt: 2, bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1.5 }}>
                        💳 Credit & Payment Information
                      </Typography>
                      
                      {/* Auto-Credit Applied */}
                      {selectedInvoice.has_credit_payment && selectedInvoice.credit_applied > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            ✓ <strong>Credit Auto-Applied:</strong>
                          </Typography>
                          <Typography variant="body1" color="info.main" fontWeight="bold">
                            KES {selectedInvoice.credit_applied?.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Credit was automatically applied when this invoice was generated or updated
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Overpayment / Credit Generated */}
                      {(() => {
                        const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                        const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                        const isOverpaid = balance < 0;
                        const creditGenerated = Math.abs(balance);
                        
                        if (isOverpaid) {
                          return (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                ⚡ <strong>Overpayment Detected:</strong>
                              </Typography>
                              <Typography variant="body1" color="success.main" fontWeight="bold">
                                KES {creditGenerated.toLocaleString()} excess payment
                              </Typography>
                              <Typography variant="caption" color="success.dark" fontWeight="medium">
                                ✓ Credit stored in tenant's account for future invoices
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Payment Breakdown */}
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #90caf9' }}>
                        <Typography variant="caption" color="textSecondary" fontWeight="bold">
                          PAYMENT BREAKDOWN:
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption">Manual Payments:</Typography>
                          <Typography variant="caption" fontWeight="bold">
                            KES {((selectedInvoice.amount_paid || 0) - (selectedInvoice.credit_applied || 0)).toLocaleString()}
                          </Typography>
                        </Box>
                        {selectedInvoice.has_credit_payment && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="info.main">Auto-Credit:</Typography>
                            <Typography variant="caption" fontWeight="bold" color="info.main">
                              KES {(selectedInvoice.credit_applied || 0).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px dashed #90caf9' }}>
                          <Typography variant="caption" fontWeight="bold">Total Paid:</Typography>
                          <Typography variant="caption" fontWeight="bold" color="success.main">
                            KES {(selectedInvoice.amount_paid || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedInvoice && (() => {
            const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
            const balance = totalAmount - (selectedInvoice.amount_paid || 0);
            return balance > 0;
          })() && (
            <>
              <Button
                variant="outlined"
                color="info"
                onClick={() => handleApplyCredits(selectedInvoice)}
              >
                Apply Available Credits
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
                onClick={() => {
                  handleCloseViewDialog();
                  handleOpenPaymentDialog(selectedInvoice);
                }}
              >
                Record Payment
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialog} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ pt: 2 }}>
              {/* Invoice Info Summary */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Invoice #
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      #{selectedInvoice.invoice_number || selectedInvoice.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Tenant
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedInvoice.tenant_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Invoice Amount
                    </Typography>
                    <Typography variant="body2">
                      KES {selectedInvoice.amount?.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Remaining Balance
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      KES {(() => {
                        const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                        const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                        return balance.toLocaleString();
                      })()}
                    </Typography>
                  </Grid>
                  {selectedInvoice.penalty_amount > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">
                        Late Payment Penalty
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        KES {selectedInvoice.penalty_amount?.toLocaleString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Payment Form */}
              <TextField
                fullWidth
                label="Payment Amount (KES)"
                name="amount"
                type="number"
                value={paymentFormData.amount}
                onChange={handlePaymentInputChange}
                margin="normal"
                required
                helperText={(() => {
                  const totalAmount = selectedInvoice.penalty_amount > 0 ? selectedInvoice.total_with_penalty : selectedInvoice.amount;
                  const balance = totalAmount - (selectedInvoice.amount_paid || 0);
                  return `Current balance: KES ${balance.toLocaleString()} (overpayment allowed for advance credit)`;
                })()}
              />
              <TextField
                fullWidth
                select
                label="Payment Method"
                name="payment_method"
                value={paymentFormData.payment_method}
                onChange={handlePaymentInputChange}
                margin="normal"
                required
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="mpesa">M-Pesa</MenuItem>
                <MenuItem value="card">Card/Mobile Money</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label={paymentFormData.payment_method === 'mpesa' ? 'M-Pesa Transaction Code *' : 'Transaction Reference (Optional)'}
                name="transaction_ref"
                value={paymentFormData.transaction_ref}
                onChange={handlePaymentInputChange}
                margin="normal"
                required={paymentFormData.payment_method === 'mpesa'}
                placeholder={paymentFormData.payment_method === 'mpesa' ? 'e.g. SHJ7ABCDEF' : 'e.g., Bank ref, receipt number'}
                helperText={paymentFormData.payment_method === 'mpesa' ? 'Enter the Safaricom M-Pesa transaction code. Duplicate codes will be rejected.' : ''}
                error={paymentFormData.payment_method === 'mpesa' && !paymentFormData.transaction_ref.trim()}
              />
              <TextField
                fullWidth
                label="Notes (Optional - for your reference only)"
                name="notes"
                value={paymentFormData.notes}
                onChange={handlePaymentInputChange}
                margin="normal"
                multiline
                rows={2}
                placeholder="Additional notes (not sent to backend)"
                helperText="Note: Payment date is automatically set to current time"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog} disabled={paymentSubmitting}>Cancel</Button>
          <Button onClick={handleSubmitPayment} variant="contained" color="success" disabled={paymentSubmitting}>
            {paymentSubmitting ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Invoice Dialog */}
      <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Void Invoice</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                You are about to void invoice <strong>#{selectedInvoice.invoice_number}</strong> for
                tenant <strong>{selectedInvoice.tenant_name}</strong> (KES {selectedInvoice.amount?.toLocaleString()}).
              </Typography>
              {(selectedInvoice.amount_paid || 0) > 0 && (
                <Typography variant="body2" color="error" gutterBottom>
                  This invoice has KES {selectedInvoice.amount_paid?.toLocaleString()} in payments
                  that will also be reversed.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Reason for voiding"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                required
                placeholder="e.g. Duplicate invoice, generated by mistake"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialog(false)}>Cancel</Button>
          <Button onClick={handleVoidInvoice} variant="contained" color="error">
            Void Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Waiver Dialog */}
      <Dialog open={waiverDialog} onClose={() => setWaiverDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'info.main' }}>Apply Waiver</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                Apply a waiver (discount) to invoice <strong>#{selectedInvoice.invoice_number}</strong> for
                tenant <strong>{selectedInvoice.tenant_name}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Invoice amount: KES {selectedInvoice.amount?.toLocaleString()} | 
                Balance: KES {selectedInvoice.remaining_balance?.toLocaleString()}
              </Typography>
              <TextField
                fullWidth
                label="Waiver Amount (KES)"
                type="number"
                value={waiverAmount}
                onChange={(e) => setWaiverAmount(e.target.value)}
                margin="normal"
                required
                helperText={`Maximum: KES ${selectedInvoice.remaining_balance?.toLocaleString()}`}
              />
              <TextField
                fullWidth
                label="Reason for waiver"
                value={waiverReason}
                onChange={(e) => setWaiverReason(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                required
                placeholder="e.g. Goodwill discount, early payment incentive, agreed reduction"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiverDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyWaiver} variant="contained" color="info">
            Apply Waiver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;
